import { z } from "zod";
import { PublicUser } from "./friendship.js";

/** What can be reported: a person or group as a whole, or only their name or picture. */
export const REPORT_SUBJECT_TYPES = [
  "user",
  "user_name",
  "user_avatar",
  "group",
  "group_name",
  "group_avatar",
] as const;
export const ReportSubjectType = z.enum(REPORT_SUBJECT_TYPES);
export type ReportSubjectType = z.infer<typeof ReportSubjectType>;

export const REPORT_REASONS = [
  "spam",
  "harassment",
  "impersonation",
  "inappropriate",
  "other",
] as const;
export const ReportReason = z.enum(REPORT_REASONS);
export type ReportReason = z.infer<typeof ReportReason>;

/** POST /v1/reports -- answers `{ok:true}` whether or not the subject exists, so a report never
 * confirms that an id belongs to anyone. */
export const CreateReportRequest = z.object({
  subjectType: ReportSubjectType,
  subjectId: z.string().min(1).max(128),
  reason: ReportReason,
  note: z.string().max(500).optional(),
});
export type CreateReportRequest = z.infer<typeof CreateReportRequest>;

export const RelationshipState = z.enum([
  "none",
  "friends",
  "request_sent",
  "request_received",
  "blocked",
]);
export type RelationshipState = z.infer<typeof RelationshipState>;

export const MutualGroup = z.object({
  id: z.string().min(1),
  name: z.string(),
  avatarUrl: z.url().nullable(),
});
export type MutualGroup = z.infer<typeof MutualGroup>;

/**
 * GET /v1/users/:userId/profile -- the person behind a row, as the viewer may see them: identity
 * fields only (never an email), how the two stand to each other, and only what both share. The
 * lists are capped; the counts are not. A person who blocked the viewer answers 404, like a
 * person who does not exist.
 */
export const PersonProfile = z.object({
  user: PublicUser,
  relationship: z.object({
    state: RelationshipState,
    /** The friendship row to accept, decline, cancel or end; null when there is none. */
    friendshipId: z.string().min(1).nullable(),
  }),
  mutualFriends: z.array(PublicUser),
  mutualFriendCount: z.number().int().nonnegative(),
  mutualGroups: z.array(MutualGroup),
  mutualGroupCount: z.number().int().nonnegative(),
});
export type PersonProfile = z.infer<typeof PersonProfile>;
